import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Logo } from '../components/ui'
import { useMe } from '../hooks/useMe'
import '../styles/landing.css'

const STEPS: [string, string][] = [
  ['Make a home for your community', 'Sign in, name your community and add a show. Invite the people who run it with you.'],
  ['Go live from OBS', 'Every show gets its own stream key. Start streaming the way you already do.'],
  ['Put the player on your site', 'Paste one line into your own page. Viewers watch there, not on someone else’s platform.'],
  ['Every stream becomes an episode', 'The recording is playable straight away. Ask for a transcript, show notes, chapters and clips, and it has a podcast feed.'],
]

const PILLARS: { title: string; body: string; points: string[] }[] = [
  {
    title: 'Your site, your audience',
    body: 'The player lives on your pages, under your name. There’s no feed of other channels and no algorithm pulling your viewers away.',
    points: ['Live player and episode player to embed', 'Public show pages if you want them', 'Podcast feed for every show'],
  },
  {
    title: 'Sponsor spots you sell',
    body: 'Add your own sponsors before or after your episodes, per show and with start and end dates. Your site decides who hears them, so members can skip them.',
    points: ['Pre-roll and post-roll spots', 'Levelled to the same loudness as your show', 'A clean copy is always kept for members'],
  },
  {
    title: 'Everything after the stream',
    body: 'The work that usually eats a weekend takes one click each, and you only pay for what you ask for.',
    points: ['Transcripts in 15+ languages, with captions', 'Show notes, chapters and ready-to-post clips', 'Audio clean-up and listener numbers'],
  },
]

/** indiefuture.com's front door: what it is, for the people who run community sites. */
export function LandingPage() {
  const me = useMe()
  const signedIn = Boolean(me.data?.authenticated)
  useEffect(() => {
    document.title = 'indiefuture: live streaming for community sites'
  }, [])

  return (
    <div className="landing">
      <header className="landing-bar">
        <div className="landing-wrap landing-bar-inner">
          <Link to="/" className="brand">
            <Logo /> indiefuture
          </Link>
          <nav className="landing-nav" aria-label="Main">
            <a href="#how">How it works</a>
            <a href="/docs">API</a>
          </nav>
          {signedIn ? (
            <Link className="btn btn-primary" to="/studio">Open your studio</Link>
          ) : (
            <div className="landing-bar-actions">
              <Link className="btn btn-ghost" to="/login">Log in</Link>
              <Link className="btn btn-primary" to="/login?return_to=%2Fstudio">Get started</Link>
            </div>
          )}
        </div>
      </header>

      <main>
        <section className="landing-wrap landing-hero">
          <div className="landing-hero-copy">
            <p className="landing-eyebrow">For communities with a home of their own</p>
            <h1>Live streams that live on your site.</h1>
            <p className="landing-lede">
              indiefuture is the streaming and publishing engine behind community websites. Go live from OBS, show the
              player on your own pages, sell your own sponsor spots, and let your members skip them.
            </p>
            <div className="landing-cta">
              <Link className="btn btn-primary btn-lg" to={signedIn ? '/studio' : '/login?return_to=%2Fstudio'}>
                {signedIn ? 'Open your studio' : 'Start your community'}
              </Link>
              <a className="btn btn-secondary btn-lg" href="#how">See how it works</a>
            </div>
          </div>
          <SiteMock />
        </section>

        <section className="landing-band" id="how" aria-labelledby="how-title">
          <div className="landing-wrap">
            <h2 id="how-title" className="landing-h2">From “we should stream” to a show people can find</h2>
            <ol className="landing-steps">
              {STEPS.map(([title, body], i) => (
                <li key={title}>
                  <span className="landing-step-n" aria-hidden="true">{i + 1}</span>
                  <h3>{title}</h3>
                  <p>{body}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="landing-wrap landing-pillars" aria-label="What you control">
          {PILLARS.map((pillar) => (
            <article key={pillar.title} className="landing-pillar">
              <h3>{pillar.title}</h3>
              <p>{pillar.body}</p>
              <ul>
                {pillar.points.map((point) => <li key={point}>{point}</li>)}
              </ul>
            </article>
          ))}
        </section>

        <section className="landing-wrap landing-split">
          <div>
            <h2 className="landing-h2">Built for positive communities</h2>
            <p>
              indiefuture is for parents’ circles, study groups, faith communities, local clubs and support groups:
              people who already have a home online and want their streams to live there, among the people who make
              it worth showing up.
            </p>
            <p className="muted">
              A public directory of communities is on the way. Listing will be opt-in and reviewed, so it stays a
              place worth browsing.
            </p>
          </div>
          <div className="landing-code" aria-label="Embedding the player">
            <p className="landing-code-label">On your site</p>
            <pre><code>{`<iframe
  src="https://indiefuture.com/embed/…"
  title="Sunday circle"
  allow="autoplay; fullscreen"
></iframe>`}</code></pre>
            <p className="landing-code-note">
              Your site talks to indiefuture through a documented <a href="/docs">API</a> and signed webhooks, so it
              hears the moment a transcript or clip is ready.
            </p>
          </div>
        </section>

        <section className="landing-band landing-final">
          <div className="landing-wrap landing-final-inner">
            <h2 className="landing-h2">Bring your community’s streams home.</h2>
            <Link className="btn btn-primary btn-lg" to={signedIn ? '/studio' : '/login?return_to=%2Fstudio'}>
              {signedIn ? 'Open your studio' : 'Get started'}
            </Link>
          </div>
        </section>
      </main>

      <footer className="landing-wrap landing-footer">
        <span className="brand"><Logo size={18} /> indiefuture</span>
        <nav aria-label="Footer">
          <a href="/docs">API reference</a>
          <Link to="/login">Log in</Link>
        </nav>
        <span className="muted">© {new Date().getFullYear()} indiefuture</span>
      </footer>
    </div>
  )
}

/** A community's own page with the player on it: what the product looks like from outside. */
function SiteMock() {
  return (
    <figure className="site-mock" aria-label="Example: a community site with the indiefuture player on it">
      <div className="site-mock-bar">
        <span className="site-mock-dots" aria-hidden="true"><i /><i /><i /></span>
        <span className="site-mock-url">yourcommunity.org/live</span>
      </div>
      <div className="site-mock-page">
        <div className="site-mock-nav">
          <strong>Your Community</strong>
          <span>Shows</span><span>Members</span><span>Live</span>
        </div>
        <div className="site-mock-player">
          <span className="site-mock-live">LIVE</span>
          <span className="site-mock-viewers">142 watching</span>
          <div className="site-mock-title">Sunday circle: raising kids who talk about money</div>
        </div>
        <div className="site-mock-episode">
          <span className="site-mock-episode-title">Last week · Ep. 11: When they ask for a phone</span>
          <span className="site-mock-tags"><span>Transcript</span><span>Chapters</span><span>4 clips</span></span>
        </div>
        <div className="site-mock-lines" aria-hidden="true"><i /><i /></div>
      </div>
      <figcaption>Illustration. Your site, your design, the indiefuture player.</figcaption>
    </figure>
  )
}
